import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment, ValidationError } from '@netfoundry-ui/shared/model';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { OidcPrompt, oidcPromptTitle, OidcProviderV3 } from './frontdoor-v3-oidc-provider.service';
import { TcpShareListFilters, withShareFilters } from './frontdoor-v3-share.service';
import { ShareHealthV3 } from './frontdoor-v3-share-health';
import { checkFrontdoorBody } from './frontdoor-v3-validation';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';

/** An HTTP share as the v3 API returns it. It answers at `url`, which is `https://<name>.<domain name>`. */
export interface HttpShareV3 {
    [key: string]: any;

    id: string;
    frontdoorId?: string;
    /** The `VERIFIED` domain name the share answers under. */
    domainNameId?: string;
    activeAgentIds?: string[];
    passiveAgentIds?: string[];
    /** One DNS label, unique within its domain name. */
    name?: string;
    /** `<name>.<domain name>` in lowercase. */
    hostname?: string;
    /** The URL a client opens. It answers once the share is `DEPLOYED`. */
    url?: string;
    description?: string;
    /** The service the hosting agents forward to. It receives plain HTTP, or HTTPS when the share has `tls`. */
    targetAddress?: string;
    targetPort?: number;
    /** The Host header the service receives, and with `tls` the SNI and certificate name. Absent means `hostname`. */
    virtualHost?: string;
    /** TLS from the ingress to the service, or absent when the service receives plain HTTP. A read always carries `verify`. */
    tls?: HttpShareTlsV3;
    zitiServiceId?: string;
    /** The provider of the login policy, or absent on a public share. */
    oidcProviderId?: string;
    /** The login policy, or absent on a public share. */
    oidc?: HttpShareOidcV3;
    /** The redirect URI to register at the provider, the same for every share. Present on a share with a login policy. */
    callbackUrl?: string;
    /** `NEW`, `PENDING_DEPLOY`, `DEPLOYED`, `DEPLOY_FAILED`, `ERROR`, `DELETING` or `DELETED`. Read it through {@link httpShareStatus}. */
    status?: string;
    createdAt?: string;
    updatedAt?: string;
    /** Where the share stands, from the port check each host runs. Absent on a create response. */
    health?: ShareHealthV3 | null;
}

/**
 * The login policy of a share: the account's provider that logs users in, and who may pass. A user passes when any one allow list
 * matches, so the service needs at least one of them. Bypass paths are served without login.
 */
export interface HttpShareOidcV3 {
    providerId: string;
    allowedEmailDomains?: string[];
    allowedEmails?: string[];
    allowedGroups?: string[];
    bypassPaths?: string[];
    /** Which requests keep their Authorization header. A read always carries it; an update that omits it keeps the stored value. */
    forwardAuthorization?: HttpShareForwardAuthorization;
    /** The prompt sent in place of the provider's. `OMIT` sends none, and absent or null uses the provider's. A PATCH that omits it keeps the stored value. */
    prompt?: OidcPrompt | null;
}

/** How the ingress speaks HTTPS to the service. `{}` verifies the certificate against the public CA roots. */
export interface HttpShareTlsV3 {
    /** False accepts any certificate. Absent means true. The certificate must match the share's virtual host. */
    verify?: boolean;
}

/** Which requests of a protected share keep their Authorization header on the way to the service. The ingress removes it from the others. */
export type HttpShareForwardAuthorization = 'NEVER' | 'ALWAYS';

/** The service's default. */
export const DEFAULT_FORWARD_AUTHORIZATION: HttpShareForwardAuthorization = 'NEVER';

/** The forwarding choices, in the order the form offers them. */
export const HTTP_SHARE_FORWARD_AUTHORIZATIONS: {
    value: HttpShareForwardAuthorization;
    title: string;
    detail: string;
}[] = [
    {
        value: 'NEVER',
        title: 'Remove the header',
        detail: 'The service never gets the Authorization header, including paths without login.',
    },
    {
        value: 'ALWAYS',
        title: 'Always forward the header',
        detail: 'Every request keeps the Authorization header. Use this when the service checks its own tokens.',
    },
];

/** The title of `value`, or '' when there is no value to name. */
export function forwardAuthorizationTitle(value: HttpShareForwardAuthorization | null | undefined): string {
    if (!value) {
        return '';
    }
    return HTTP_SHARE_FORWARD_AUTHORIZATIONS.find((option) => option.value === value)?.title ?? value;
}

export interface CreateHttpShareV3 {
    domainNameId: string;
    activeAgentIds: string[];
    passiveAgentIds?: string[];
    name: string;
    targetAddress: string;
    targetPort: number;
    description?: string;
    /** Absent means the share's hostname. */
    virtualHost?: string;
    /** Absent makes a public share. */
    oidc?: HttpShareOidcV3;
    /** Absent means plain HTTP to the service. */
    tls?: HttpShareTlsV3;
}

/**
 * A `PATCH` body. A sent list replaces the stored one.
 * `null` clears `passiveAgentIds`, `description`, `virtualHost` and `oidc`.
 */
export interface UpdateHttpShareV3 {
    name?: string;
    domainNameId?: string;
    activeAgentIds?: string[];
    passiveAgentIds?: string[] | null;
    targetAddress?: string;
    targetPort?: number;
    description?: string | null;
    /** `null` goes back to the share's hostname, and an omitted field keeps the stored value. */
    virtualHost?: string | null;
    /** Replaces the whole policy. `null` makes the share public, and an omitted field keeps the stored policy. */
    oidc?: HttpShareOidcV3 | null;
    /** Replaces the whole object. `null` goes back to plain HTTP, and an omitted field keeps the stored value. */
    tls?: HttpShareTlsV3 | null;
}

/** One page of HTTP shares, plus the link that gets the next one. */
export interface HttpSharePageV3 {
    shares: HttpShareV3[];
    /** The service's own link to the next page, or '' at the end. */
    nextHref: string;
    /** Total across all pages when the service reports one, otherwise null. */
    total: number | null;
}

/** The TCP filters, plus the domain names and the OIDC Providers to list the shares of. */
export interface HttpShareListFilters extends TcpShareListFilters {
    domainNameIds?: string[];
    oidcProviderIds?: string[];
}

export const DEFAULT_HTTP_SHARE_PAGE_SIZE = 50;

/** Enough HTTP shares to count what every agent of one frontdoor hosts in a single read, as for TCP shares. */
export const HTTP_SHARE_COUNT_PAGE_SIZE = 500;

/** The query parameter on the HTTP Shares page that opens the drawer on one share. */
export const FD_HTTP_SHARE_QUERY_PARAM = 'share';

/** Filter ids of the HTTP Shares page that other pages link to. `f.<id>` on the URL sets the filter. */
export const FD_HTTP_SHARE_DOMAIN_NAME_FILTER = 'domainName';
export const FD_HTTP_SHARE_AUTH_PROVIDER_FILTER = 'authProvider';

/** One or more labels of letters, digits and inner hyphens. */
const DNS_NAME = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;

/** A dotted quad without leading zeros. */
const IPV4 = /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/;

/** The `oidcProviderId` value that lists the shares without a login policy. */
export const NO_OIDC_PROVIDER_ID = 'none';

/** The query parameters that open the HTTP Shares page with one filter set. The list table reads `f.<id>` as a filter. */
export function httpSharesFilteredBy(filterId: string, value: string): Record<string, string> {
    return { [`f.${filterId}`]: value };
}

/** The query parameter on the create page that preselects a domain name. */
export const FD_HTTP_SHARE_DOMAIN_NAME_QUERY_PARAM = 'domainNameId';

/** The URL fragment on the edit page that scrolls the form to its Authentication panel. */
export const FD_HTTP_SHARE_AUTHENTICATION_FRAGMENT = 'authentication';

/** The longest name the service accepts: one DNS label. */
export const MAX_HTTP_SHARE_NAME_LENGTH = 63;

/** `deployFailed` is a built share that the account's ingress does not route. */
export type HttpShareStatus =
    | 'new'
    | 'pendingDeploy'
    | 'deployed'
    | 'deployFailed'
    | 'error'
    | 'deleting'
    | 'deleted'
    | 'unknown';

/** How a status reads on screen. `hint` goes in a tooltip, and is '' where the label says it all. */
export interface HttpShareStatusBadge {
    tone: 'ok' | 'warn' | 'err' | 'muted';
    label: string;
    hint: string;
}

/** The tooltip on a deploying share. The API does not expose the ingress, so the share cannot tell which wait it is in. */
export const HTTP_SHARE_DEPLOY_HINT =
    'The first HTTP share of an account builds its HTTPS ingress, which takes several minutes. Later shares deploy in seconds.';

const HTTP_SHARE_STATUS_BADGES: Record<HttpShareStatus, HttpShareStatusBadge> = {
    new: { tone: 'warn', label: 'New', hint: HTTP_SHARE_DEPLOY_HINT },
    pendingDeploy: { tone: 'warn', label: 'Deploying', hint: HTTP_SHARE_DEPLOY_HINT },
    deployed: { tone: 'ok', label: 'Deployed', hint: '' },
    deployFailed: {
        tone: 'err',
        label: 'Deploy Failed',
        hint:
            'The HTTPS ingress of this account is not ready. ' +
            'The next ingress build publishes the share, and Retry builds it again now.',
    },
    error: { tone: 'err', label: 'Error', hint: 'The build stopped part way. Retry builds the share again.' },
    deleting: { tone: 'warn', label: 'Deleting', hint: '' },
    deleted: { tone: 'muted', label: 'Deleted', hint: '' },
    unknown: { tone: 'muted', label: 'Unknown', hint: '' },
};

const HTTP_SHARE_WIRE_STATUSES = new Map<string, HttpShareStatus>([
    ['NEW', 'new'],
    ['PENDING_DEPLOY', 'pendingDeploy'],
    ['DEPLOYED', 'deployed'],
    ['DEPLOY_FAILED', 'deployFailed'],
    ['ERROR', 'error'],
    ['DELETING', 'deleting'],
    ['DELETED', 'deleted'],
]);

/** The statuses a share is still moving through. The list polls while a row is in one. */
const TRANSITIONING: HttpShareStatus[] = ['new', 'pendingDeploy', 'deleting'];

const REBUILDABLE: HttpShareStatus[] = ['deployFailed', 'error'];

/** The statuses a filter offers, in build order. `deleted` is left out because the listing never shows one. */
export const HTTP_SHARE_STATUS_OPTIONS: HttpShareStatus[] = [
    'new',
    'pendingDeploy',
    'deployed',
    'deployFailed',
    'error',
    'deleting',
];

export function httpShareStatus(share: HttpShareV3 | undefined): HttpShareStatus {
    return HTTP_SHARE_WIRE_STATUSES.get((share?.status ?? '').toUpperCase()) ?? 'unknown';
}

export function httpShareStatusBadge(status: HttpShareStatus): HttpShareStatusBadge {
    return HTTP_SHARE_STATUS_BADGES[status] ?? HTTP_SHARE_STATUS_BADGES.unknown;
}

/** `unknown` has no wire spelling, so it is ''. */
export function httpShareWireStatus(status: HttpShareStatus): string {
    for (const [wire, known] of HTTP_SHARE_WIRE_STATUSES) {
        if (known === status) {
            return wire;
        }
    }
    return '';
}

export function isHttpShareRebuildable(share: HttpShareV3 | undefined): boolean {
    return REBUILDABLE.includes(httpShareStatus(share));
}

/** True while the service still moves the share. It answers 409 to an update in these states. */
export function isHttpShareTransitioning(share: HttpShareV3 | undefined): boolean {
    return TRANSITIONING.includes(httpShareStatus(share));
}

/** The provider that logs users in to `share`, from its policy or its top-level id, or '' for a public share. */
export function httpShareProviderId(share: HttpShareV3 | null | undefined): string {
    return share?.oidc?.providerId ?? share?.oidcProviderId ?? '';
}

/** The redirect URI the provider must accept for `share`, or '' for a public share. It is the same for every share. */
export function httpShareCallbackUrl(share: HttpShareV3 | null | undefined): string {
    return share?.callbackUrl ?? '';
}

/**
 * The login policy of `share`, or undefined for a public share. A share that names only `oidcProviderId` gets a policy
 * with no allow lists.
 */
export function httpShareLoginPolicy(share: HttpShareV3 | null | undefined): HttpShareOidcV3 | undefined {
    const providerId = share?.oidcProviderId;
    return share?.oidc ?? (providerId ? { providerId } : undefined);
}

/**
 * The login prompt of `share` as a reader sees it, marked when it comes from `provider`. A read without a policy
 * answers '', because the share's own prompt is then not known. A policy without a prompt uses the provider's.
 */
export function httpShareLoginPrompt(share: HttpShareV3 | null | undefined, provider: OidcProviderV3 | null): string {
    const oidc = share?.oidc;
    if (!oidc) {
        return '';
    }
    if (oidc.prompt === 'OMIT') {
        return "No prompt (replaces the OIDC Provider's)";
    }
    if (oidc.prompt) {
        return oidcPromptTitle(oidc.prompt);
    }
    if (!provider) {
        return 'From the OIDC Provider (not readable)';
    }
    return `${oidcPromptTitle(provider.prompt) || 'No prompt'} (from OIDC Provider)`;
}

/**
 * True when the ingress accepts `name` as a virtual host: an IP address without brackets or zone, or a DNS name such
 * as localhost with no wildcard, port or trailing dot. A last label of digits alone reads as a mistyped IP address,
 * so it is refused. Mirrors the service's check.
 */
export function isVirtualHost(name: string | null | undefined): boolean {
    if (!name || name.length > 253) {
        return false;
    }
    if (IPV4.test(name) || isIpv6(name)) {
        return true;
    }
    return DNS_NAME.test(name) && !/^[0-9]+$/.test(name.slice(name.lastIndexOf('.') + 1));
}

/** The name the service receives as its Host header: the virtual host, or else the share's hostname. */
export function httpShareVirtualHost(share: HttpShareV3 | null | undefined): string {
    return share?.virtualHost || share?.hostname || '';
}

/** `https://address:port` or `http://address:port`, or '' where the share names no target. */
export function httpShareTargetUrl(share: HttpShareV3 | null | undefined): string {
    const address = share?.targetAddress ?? '';
    if (!address) {
        return '';
    }
    const host = address.includes(':') ? `[${address}]` : address;
    const port = share?.targetPort ? `:${share.targetPort}` : '';
    return `${share?.tls ? 'https' : 'http'}://${host}${port}`;
}

/** True for an HTTPS share that accepts any certificate. */
export function httpShareSkipsVerification(share: HttpShareV3 | null | undefined): boolean {
    return share?.tls?.verify === false;
}

/** How the ingress checks the service's certificate, or '' for a share over plain HTTP. */
export function httpShareTlsSummary(share: HttpShareV3 | null | undefined): string {
    const tls = share?.tls;
    if (!tls) {
        return '';
    }
    const name = httpShareVirtualHost(share);
    if (httpShareSkipsVerification(share)) {
        return name ? `Not verified (virtual host ${name})` : 'Not verified';
    }
    return name ? `Verified for ${name}` : 'Verified';
}

/** The domain name part of the share's hostname, so a page can name the domain name without reading it. */
export function httpShareDomainName(share: HttpShareV3 | null | undefined): string {
    const hostname = share?.hostname ?? '';
    const prefix = `${(share?.name ?? '').toLowerCase()}.`;
    return prefix.length > 1 && hostname.startsWith(prefix) ? hostname.slice(prefix.length) : hostname;
}

/** Adds the filters a listing asks for. An absent or empty filter is left off. */
export function withHttpShareFilters(params: HttpParams, filters: HttpShareListFilters = {}): HttpParams {
    let withFilters = withShareFilters(params, filters);
    for (const domainNameId of filters.domainNameIds ?? []) {
        if (domainNameId) {
            withFilters = withFilters.append('domainNameId', domainNameId);
        }
    }
    for (const oidcProviderId of filters.oidcProviderIds ?? []) {
        if (oidcProviderId) {
            withFilters = withFilters.append('oidcProviderId', oidcProviderId);
        }
    }
    return withFilters;
}

/** Reads and writes the HTTP shares of one v3 frontdoor. Every path is scoped by the frontdoor id. */
@Injectable({ providedIn: 'root' })
export class FrontdoorV3HttpShareService {
    private readonly apiUrl: string;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = this.environment.frontdoorURL;
    }

    /** One page of `frontdoorId`'s HTTP shares. The service pages by offset, so `page` counts from 0. */
    public findHttpShares(
        frontdoorId: string,
        sort = 'createdAt,DESC',
        size = DEFAULT_HTTP_SHARE_PAGE_SIZE,
        filters: HttpShareListFilters = {},
        page = 0
    ): Observable<HttpSharePageV3> {
        const params = withHttpShareFilters(
            new HttpParams().set('sort', sort).set('size', String(size)).set('page', String(page)),
            filters
        );
        return this.readPageAt(this.sharesUrl(frontdoorId), params);
    }

    /** The first page of the shares published under `domainNameId`, sorted by name. */
    public findSharesForDomainName(
        frontdoorId: string,
        domainNameId: string,
        sort = 'name,ASC',
        size = DEFAULT_HTTP_SHARE_PAGE_SIZE
    ): Observable<HttpSharePageV3> {
        // An empty id names no share. Without this check the filter is dropped and every share is listed.
        if (!domainNameId) {
            return of(emptySharePage());
        }
        return this.findHttpShares(frontdoorId, sort, size, { domainNameIds: [domainNameId] });
    }

    /** The first page of the shares whose login policy names `oidcProviderId`, sorted by name. */
    public findSharesForOidcProvider(
        frontdoorId: string,
        oidcProviderId: string,
        sort = 'name,ASC',
        size = DEFAULT_HTTP_SHARE_PAGE_SIZE
    ): Observable<HttpSharePageV3> {
        if (!oidcProviderId) {
            return of(emptySharePage());
        }
        return this.findHttpShares(frontdoorId, sort, size, { oidcProviderIds: [oidcProviderId] });
    }

    /** The next page, by following the link the service returned. */
    public followNext(nextHref: string): Observable<HttpSharePageV3> {
        return this.readPageAt(nextHref);
    }

    public getHttpShare(frontdoorId: string, id: string): Observable<HttpShareV3> {
        return this.http
            .get(this.shareUrl(frontdoorId, id), { headers: this.headers() })
            .pipe(map((response: any) => response as HttpShareV3));
    }

    /** Adds a share in `NEW`. The body repeats the frontdoor id, because the service refuses a body that names another. */
    public createHttpShare(frontdoorId: string, create: CreateHttpShareV3): Observable<HttpShareV3> {
        return this.http
            .post(this.sharesUrl(frontdoorId), { ...create, frontdoorId }, { headers: this.headers() })
            .pipe(map((response: any) => response as HttpShareV3));
    }

    public validateCreateHttpShare(frontdoorId: string, create: CreateHttpShareV3): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, this.sharesUrl(frontdoorId), { ...create, frontdoorId });
    }

    /** Applies `update` with a `PATCH`. Any update to a share in `ERROR` builds it again, even an empty one. */
    public updateHttpShare(frontdoorId: string, id: string, update: UpdateHttpShareV3): Observable<HttpShareV3> {
        return this.http
            .patch(this.shareUrl(frontdoorId, id), update, { headers: this.headers() })
            .pipe(map((response: any) => response as HttpShareV3));
    }

    /** Whether {@link updateHttpShare} would be accepted. Uses `PATCH`, the same method as the write. */
    public validateUpdateHttpShare(
        frontdoorId: string,
        id: string,
        update: UpdateHttpShareV3
    ): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, this.shareUrl(frontdoorId, id), update, 'PATCH');
    }

    /** Builds a share in `ERROR` again. An empty `PATCH` changes nothing else. */
    public retryHttpShare(frontdoorId: string, id: string): Observable<HttpShareV3> {
        return this.updateHttpShare(frontdoorId, id, {});
    }

    /** Takes the share out of the ingress host map and removes its Ziti objects. */
    public deleteHttpShare(frontdoorId: string, id: string): Observable<any> {
        return this.http.delete(this.shareUrl(frontdoorId, id), { headers: this.headers() });
    }

    private sharesUrl(frontdoorId: string): string {
        return `${this.apiUrl}/v3/${frontdoorId}/http-shares`;
    }

    private shareUrl(frontdoorId: string, id: string): string {
        return `${this.sharesUrl(frontdoorId)}/${id}`;
    }

    private readPageAt(url: string, params?: HttpParams): Observable<HttpSharePageV3> {
        return this.http
            .get(url, { headers: this.headers(), params })
            .pipe(map((response: any) => this.readPage(response)));
    }

    private headers(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    private readPage(response: any): HttpSharePageV3 {
        const embedded = response?._embedded;
        const shares = embedded?.httpShares ?? (embedded ? Object.values(embedded)[0] : undefined);
        if (embedded && !Array.isArray(shares)) {
            this.logger.error('findHttpShares: unrecognised response shape', response);
        }
        const rows = Array.isArray(shares) ? (shares as HttpShareV3[]) : [];
        const total = Number.isFinite(response?.page?.totalElements) ? response.page.totalElements : null;
        return { shares: rows, nextHref: response?._links?.next?.href ?? '', total };
    }
}

/** A page with no shares, for a query that cannot match any. */
function emptySharePage(): HttpSharePageV3 {
    return { shares: [], nextHref: '', total: 0 };
}

/** The URL parser validates the literal. The character check keeps text after a `]` from passing as a path. */
function isIpv6(name: string): boolean {
    if (!name.includes(':') || !/^[0-9A-Fa-f:.]+$/.test(name)) {
        return false;
    }
    try {
        return new URL(`http://[${name}]/`).hostname.length > 2;
    } catch {
        return false;
    }
}
